"use client";

import React, { useState } from "react";
import { Play } from "lucide-react";

export interface PresetData {
    label: string;
    description: string;
}

export interface MemorylayerCompilerBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    portLabel?: string;
    presets?: {
        [key: string]: PresetData;
    };
    initialLogs?: string[];
}

export default function MemorylayerCompilerBlock({
    tag = "Interactive Compiler",
    title = "Persona Settings Drawer",
    description = "We built the dashboard to configure persona models, inject text documents, adjust weights, and preview logs in real-time.",
    portLabel = "FastAPI DevPort: 8080",
    presets = {
        sherlock: {
            label: "Sherlock Settings",
            description: "Preset: Victorian Detective. Speech rule: Use old-school vocabulary. Focus heavily on logical clues. Address user as \"my colleague.\""
        },
        translator: {
            label: "Polyglot Translation Settings",
            description: "Preset: High-speed translation layer. Maps outputs to Spanish, French, Japanese, and Mandarin, preserving system persona characteristics."
        },
        coder: {
            label: "Developer Sandbox Coder Settings",
            description: "Preset: Strict software terminal compiler. Outputs formatted clean code blocks, performance complexities, and test blueprints."
        }
    },
    initialLogs = [
        "SYSTEM: Bootstrapping pgvector embedding pipelines...",
        "SYSTEM: Loaded model Llama-3-70B-Instruct weights in VRAM.",
        "SYSTEM: Sandbox container is ONLINE and listening on port 8080."
    ]
}: MemorylayerCompilerBlockProps) {
    const presetKeys = Object.keys(presets);
    const [selectedTab, setSelectedTab] = useState<string>(presetKeys[0] || "sherlock");
    const [terminalInput, setTerminalInput] = useState("");
    const [terminalLogs, setTerminalLogs] = useState<string[]>(initialLogs);

    const handleTerminalSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!terminalInput.trim()) return;
        const userInput = terminalInput;
        setTerminalLogs(prev => [...prev, `USER> ${userInput}`]);
        setTerminalInput("");
        
        setTimeout(() => {
            let reply = "SYSTEM: Unknown command. Type '--help' for available prompt settings.";
            if (userInput.toLowerCase().includes("help")) {
                reply = "SYSTEM: Available directives: --preset [persona] | --temp [val] | --clear";
            } else if (userInput.toLowerCase().includes("preset")) {
                reply = "SYSTEM: Swapping active weights. Swapped to blueprint: 'Cyberpunk Bartender V0.9'";
            } else if (userInput.toLowerCase().includes("clear")) {
                setTerminalLogs([]);
                return;
            } else {
                reply = `SYSTEM: Evaluating RAG matches. Query embedded (1536 dims). Latency 42ms.`;
            }
            setTerminalLogs(prev => [...prev, reply]);
        }, 300);
    };

    return (
        <section id="customizer" className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F0F12] border-t border-b border-zinc-900 w-full animate-fade-in" data-header-theme="light">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
                    
                    {/* Left Control Drawer */}
                    <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
                        <div>
                            {tag && (
                                <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest block mb-4">// {tag}</span>
                            )}
                            <h2 className="text-3xl md:text-4xl font-normal text-white">{title}</h2>
                            {description && (
                                <p className="text-zinc-400 text-sm leading-relaxed mt-2 font-light">
                                    {description}
                                </p>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <span className="text-[9px] font-mono text-zinc-500 uppercase block">Preset Blueprint</span>
                                <div className="flex gap-2">
                                    {presetKeys.map((tab) => (
                                        <button
                                            key={tab}
                                            onClick={() => setSelectedTab(tab)}
                                            className={`flex-1 py-2 rounded font-mono text-xs border uppercase tracking-wider transition-colors ${
                                                selectedTab === tab 
                                                    ? "bg-purple-950/40 text-purple-300 border-purple-500/30" 
                                                    : "bg-zinc-900 text-zinc-500 border-zinc-800 hover:border-zinc-700"
                                            }`}
                                        >
                                            {tab}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-xl p-5 space-y-4 min-h-[120px]">
                                {presets[selectedTab] && (
                                    <>
                                        <div className="text-[10px] font-mono text-purple-300">// {presets[selectedTab].label}</div>
                                        <div className="text-xs text-zinc-400 font-mono leading-relaxed">
                                            {presets[selectedTab].description}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <button className="flex-1 py-3.5 bg-purple-600 hover:bg-purple-500 text-white font-mono text-xs uppercase tracking-widest font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
                                <Play className="w-3.5 h-3.5" /> Initialize weights
                            </button>
                        </div>
                    </div>

                    {/* Right Terminal Console Input */}
                    <div className="lg:col-span-7 bg-[#070709] border border-zinc-800 rounded-2xl p-6 flex flex-col justify-between min-h-[400px]">
                        <div>
                            <div className="flex justify-between items-center pb-4 border-b border-zinc-900 mb-4 font-mono text-xs text-zinc-400">
                                <span className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                                    Interactive Compiler Console
                                </span>
                                <span>{portLabel}</span>
                            </div>

                            <div className="space-y-2.5 font-mono text-[11px] text-zinc-500 h-[260px] overflow-y-auto pr-2">
                                {terminalLogs.map((log, index) => (
                                    <div key={index} className={
                                        log.startsWith("USER>") 
                                            ? "text-purple-300" 
                                            : log.includes("Success") || log.includes("ONLINE")
                                                ? "text-emerald-400" 
                                                : "text-zinc-500"
                                    }>
                                        {log}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <form onSubmit={handleTerminalSubmit} className="border-t border-zinc-900 pt-4 flex gap-3">
                            <span className="text-purple-400 font-mono self-center text-xs">&gt;</span>
                            <input
                                type="text"
                                value={terminalInput}
                                onChange={(e) => setTerminalInput(e.target.value)}
                                placeholder="Execute sandbox command (e.g. --preset translator or type your query)..."
                                className="flex-1 bg-transparent border-none outline-none text-xs text-zinc-300 font-mono focus:ring-0 focus:outline-none"
                                suppressHydrationWarning={true}
                            />
                            <button type="submit" className="px-4 py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white font-mono text-xs rounded transition-colors">
                                Execute
                            </button>
                        </form>
                    </div>

                </div>
            </div>
        </section>
    );
}
